import axios from 'axios';

// Types
interface LoadingState {
  kpis: boolean;
  chiffreAffaires: boolean;
  coursesCount: boolean;
  topClients: boolean;
  topDrivers: boolean;
  repartition: boolean;
}

interface KpisState {
  coursesCount: number;
  chiffreAffaires: number;
  clientsCount: number;
  driversCount: number;
}

interface RepartitionState {
  byType: any[];
  byStatus: any[];
}

interface PeriodeOption {
  value: string;
  text: string;
}

interface PeriodesState {
  selected: string;
  options: PeriodeOption[];
}

interface StatistiquesState {
  loading: LoadingState;
  kpis: KpisState;
  chiffreAffaires: any[];
  coursesCount: any[];
  topClients: any[];
  topDrivers: any[];
  repartition: RepartitionState;
  error: string | null;
  periodes: PeriodesState;
}

const state: StatistiquesState = {
  loading: {
    kpis: false,
    chiffreAffaires: false,
    coursesCount: false,
    topClients: false,
    topDrivers: false,
    repartition: false
  },
  kpis: {
    coursesCount: 0,
    chiffreAffaires: 0,
    clientsCount: 0,
    driversCount: 0
  },
  chiffreAffaires: [],
  coursesCount: [],
  topClients: [],
  topDrivers: [],
  repartition: {
    byType: [],
    byStatus: []
  },
  error: null,
  periodes: {
    selected: 'mois',
    options: [
      { value: 'jour', text: 'Aujourd\'hui' },
      { value: 'semaine', text: 'Cette semaine' },
      { value: 'mois', text: 'Ce mois' },
      { value: 'annee', text: 'Cette année' }
    ]
  }
};

const mutations = {
  SET_LOADING(state: StatistiquesState, { key, value }: { key: keyof LoadingState, value: boolean }) {
    state.loading = {
      ...state.loading,
      [key]: value
    };
  },
  SET_KPIS(state: StatistiquesState, kpis: Partial<KpisState>) {
    state.kpis = { ...state.kpis, ...kpis };
  },
  SET_CHIFFRE_AFFAIRES(state: StatistiquesState, data: any[]) {
    state.chiffreAffaires = data;
  },
  SET_COURSES_COUNT(state: StatistiquesState, data: any[]) {
    state.coursesCount = data;
  },
  SET_TOP_CLIENTS(state: StatistiquesState, data: any[]) {
    state.topClients = data;
  },  SET_TOP_DRIVERS(state: StatistiquesState, data: any[]) {
    state.topDrivers = data;
  },
  SET_REPARTITION(state: StatistiquesState, { key, data }: { key: keyof RepartitionState, data: any[] }) {
    state.repartition = {
      ...state.repartition,
      [key]: data
    };
  },
  SET_ERROR(state: StatistiquesState, error: string | null) {
    state.error = error;
  },
  SET_PERIODE(state: StatistiquesState, periode: string) {
    state.periodes.selected = periode;
  }
};

const actions = {
  async fetchKpis({ commit }: { commit: Function }) {
    commit('SET_LOADING', { key: 'kpis', value: true });
    try {
      const response = await axios.get('/api/statistiques/kpis');
      commit('SET_KPIS', response.data);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération des KPIs');
      console.error('Error fetching KPIs:', error);
    } finally {
      commit('SET_LOADING', { key: 'kpis', value: false });
    }
  },
  
  async fetchChiffreAffaires({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'chiffreAffaires', value: true });
    try {
      const response = await axios.get(`/api/statistiques/chiffre-affaires?periode=${state.periodes.selected}`);
      commit('SET_CHIFFRE_AFFAIRES', response.data);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération du chiffre d\'affaires');
      console.error('Error fetching chiffre d\'affaires:', error);
    } finally {
      commit('SET_LOADING', { key: 'chiffreAffaires', value: false });
    }
  },
  
  async fetchCoursesCount({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'coursesCount', value: true });
    try {
      const response = await axios.get(`/api/statistiques/courses-count?periode=${state.periodes.selected}`);
      commit('SET_COURSES_COUNT', response.data);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération du nombre de courses');
      console.error('Error fetching courses count:', error);
    } finally {
      commit('SET_LOADING', { key: 'coursesCount', value: false });
    }
  },
  
  async fetchTopClients({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'topClients', value: true });
    try {
      const response = await axios.get(`/api/statistiques/top-clients?periode=${state.periodes.selected}`);
      commit('SET_TOP_CLIENTS', response.data);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération des top clients');
      console.error('Error fetching top clients:', error);
    } finally {
      commit('SET_LOADING', { key: 'topClients', value: false });
    }
  },
  
  async fetchTopDrivers({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'topDrivers', value: true });
    try {
      const response = await axios.get(`/api/statistiques/top-drivers?periode=${state.periodes.selected}`);
      commit('SET_TOP_DRIVERS', response.data);
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération des top chauffeurs');
      console.error('Error fetching top drivers:', error);
    } finally {
      commit('SET_LOADING', { key: 'topDrivers', value: false });
    }
  },
  
  async fetchRepartitionByType({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'repartition', value: true });
    try {
      const response = await axios.get(`/api/statistiques/repartition-by-type?periode=${state.periodes.selected}`);
      commit('SET_REPARTITION', { key: 'byType', data: response.data });
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération de la répartition par type');
      console.error('Error fetching repartition by type:', error);
    } finally {
      commit('SET_LOADING', { key: 'repartition', value: false });
    }
  },
  
  async fetchRepartitionByStatus({ commit, state }: { commit: Function, state: StatistiquesState }) {
    commit('SET_LOADING', { key: 'repartition', value: true });
    try {
      const response = await axios.get(`/api/statistiques/repartition-by-status?periode=${state.periodes.selected}`);
      commit('SET_REPARTITION', { key: 'byStatus', data: response.data });
    } catch (error: any) {
      commit('SET_ERROR', error.message || 'Erreur lors de la récupération de la répartition par statut');
      console.error('Error fetching repartition by status:', error);
    } finally {
      commit('SET_LOADING', { key: 'repartition', value: false });
    }
  },
  
  setPeriode({ commit, dispatch }: { commit: Function, dispatch: Function }, periode: string) {
    commit('SET_PERIODE', periode);
    // Rafraîchir les données avec la nouvelle période
    dispatch('fetchChiffreAffaires');
    dispatch('fetchCoursesCount');
    dispatch('fetchTopClients');
    dispatch('fetchTopDrivers');
    dispatch('fetchRepartitionByType');
    dispatch('fetchRepartitionByStatus');
  },
  
  fetchAllData({ dispatch }: { dispatch: Function }) {
    dispatch('fetchKpis');
    dispatch('fetchChiffreAffaires');
    dispatch('fetchCoursesCount');
    dispatch('fetchTopClients');
    dispatch('fetchTopDrivers');
    dispatch('fetchRepartitionByType');
    dispatch('fetchRepartitionByStatus');
  }
};

const getters = {
  isLoading: (state: StatistiquesState) => (key: keyof LoadingState) => state.loading[key],
  kpis: (state: StatistiquesState) => state.kpis,
  chiffreAffaires: (state: StatistiquesState) => state.chiffreAffaires,
  coursesCount: (state: StatistiquesState) => state.coursesCount,
  topClients: (state: StatistiquesState) => state.topClients,
  topDrivers: (state: StatistiquesState) => state.topDrivers,
  repartitionByType: (state: StatistiquesState) => state.repartition.byType,
  repartitionByStatus: (state: StatistiquesState) => state.repartition.byStatus,
  periodes: (state: StatistiquesState) => state.periodes,
  selectedPeriode: (state: StatistiquesState) => state.periodes.selected
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
